/**
 * MANAGEMENT (14) / Support WORKFLOW — the workflow-enhancement i18n strings (FR default + AR-RTL),
 * registered ADDITIVELY in a NEW idempotent namespace (the support screen's support.messages.ts owns the
 * inbox/chat strings; this file adds the triage/assignment/status/SLA/resolution strings). Registered once
 * from the SupportPage on mount. Missing AR key → FR value → raw key (never a crash, never a blank).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── triage panel (assignment + status/priority management) ────────────────────────
  "mgmt.support.wf.triage.title": "Traitement du ticket",
  "mgmt.support.wf.triage.assignee": "Assigné à",
  "mgmt.support.wf.triage.assignee.none": "Non assigné",
  "mgmt.support.wf.triage.assignee.me": "M'assigner ce ticket",
  "mgmt.support.wf.triage.assignee.unassign": "Retirer l'assignation",
  "mgmt.support.wf.triage.assignee.current": "Assigné à l'agent {id}",
  "mgmt.support.wf.triage.assign": "Assigner",
  "mgmt.support.wf.triage.assigned": "Ticket assigné.",
  "mgmt.support.wf.triage.reassigned": "Ticket réassigné (précédent : agent {prev}).",
  "mgmt.support.wf.triage.unassigned": "Assignation retirée.",
  "mgmt.support.wf.triage.statut": "Statut",
  "mgmt.support.wf.triage.statut.change": "Changer le statut",
  "mgmt.support.wf.triage.statut.changed": "Statut mis à jour → {statut}.",
  "mgmt.support.wf.triage.statut.illegal": "Transition non autorisée depuis « {from} ».",
  "mgmt.support.wf.triage.priorite": "Priorité",
  "mgmt.support.wf.triage.priorite.readonly": "La priorité est définie par le laboratoire à la création.",
  "mgmt.support.wf.triage.conflict": "Le ticket a changé entre-temps — rechargez puis réessayez.",
  "mgmt.support.wf.triage.error": "L'action a échoué. Réessayez.",
  "mgmt.support.wf.triage.version": "Version {v}",

  // ── resolution action ─────────────────────────────────────────────────────────────
  "mgmt.support.wf.resolve": "Résoudre le ticket",
  "mgmt.support.wf.resolve.title": "Résoudre le ticket {ref}",
  "mgmt.support.wf.resolve.note.label": "Note de résolution",
  "mgmt.support.wf.resolve.note.placeholder": "Décrivez la résolution communiquée au laboratoire…",
  "mgmt.support.wf.resolve.note.hint": "La note est ajoutée au fil de discussion, visible par le laboratoire.",
  "mgmt.support.wf.resolve.note.required": "Une note de résolution est requise.",
  "mgmt.support.wf.resolve.confirm": "Marquer comme résolu",
  "mgmt.support.wf.resolve.cancel": "Annuler",
  "mgmt.support.wf.resolve.done": "Ticket résolu.",
  "mgmt.support.wf.resolve.closed": "Ce ticket est clôturé — rouvrez-le avant de le résoudre.",

  // ── SLA-breach strip ──────────────────────────────────────────────────────────────
  "mgmt.support.wf.sla.title": "Dépassements de SLA (cross-lab)",
  "mgmt.support.wf.sla.none": "Aucun dépassement de SLA. Tous les tickets ouverts sont dans les délais.",
  "mgmt.support.wf.sla.scanned": "Tickets ouverts analysés",
  "mgmt.support.wf.sla.breached": "En dépassement",
  "mgmt.support.wf.sla.emailed": "Alertes envoyées",
  "mgmt.support.wf.sla.col.reference": "Référence",
  "mgmt.support.wf.sla.col.sujet": "Sujet",
  "mgmt.support.wf.sla.col.priorite": "Priorité",
  "mgmt.support.wf.sla.col.overdue": "Retard",
  "mgmt.support.wf.sla.col.due": "Échéance",
  "mgmt.support.wf.sla.col.emailed": "Alerté",
  "mgmt.support.wf.sla.overdue.hours": "{h} h de retard",
  "mgmt.support.wf.sla.overdue.aria": "Ticket {ref} en dépassement de {h} heures",
  "mgmt.support.wf.sla.emailed.yes": "Oui",
  "mgmt.support.wf.sla.emailed.no": "Non",
  "mgmt.support.wf.sla.notify": "Notifier les laboratoires",
  "mgmt.support.wf.sla.notifying": "Envoi en cours…",
  "mgmt.support.wf.sla.notified": "{n} alerte(s) SLA envoyée(s).",
  "mgmt.support.wf.sla.notified.none": "Aucune nouvelle alerte à envoyer.",
  "mgmt.support.wf.sla.notify.error": "L'envoi des alertes a échoué. Réessayez.",
  "mgmt.support.wf.sla.open": "Ouvrir",
  "mgmt.support.wf.sla.more": "et {n} autre(s) ticket(s) en dépassement",
};

const AR: Record<string, string> = {
  "mgmt.support.wf.triage.title": "معالجة التذكرة",
  "mgmt.support.wf.triage.assignee": "مُسندة إلى",
  "mgmt.support.wf.triage.assignee.none": "غير مُسندة",
  "mgmt.support.wf.triage.assignee.me": "إسنادها إليّ",
  "mgmt.support.wf.triage.assignee.unassign": "إلغاء الإسناد",
  "mgmt.support.wf.triage.assignee.current": "مُسندة إلى العون {id}",
  "mgmt.support.wf.triage.assign": "إسناد",
  "mgmt.support.wf.triage.assigned": "تم إسناد التذكرة.",
  "mgmt.support.wf.triage.reassigned": "تمت إعادة الإسناد (السابق: العون {prev}).",
  "mgmt.support.wf.triage.unassigned": "تم إلغاء الإسناد.",
  "mgmt.support.wf.triage.statut": "الحالة",
  "mgmt.support.wf.triage.statut.change": "تغيير الحالة",
  "mgmt.support.wf.triage.statut.changed": "تم تحديث الحالة ← {statut}.",
  "mgmt.support.wf.triage.statut.illegal": "انتقال غير مسموح من « {from} ».",
  "mgmt.support.wf.triage.priorite": "الأولوية",
  "mgmt.support.wf.triage.priorite.readonly": "تُحدَّد الأولوية من قبل المختبر عند الإنشاء.",
  "mgmt.support.wf.triage.conflict": "تغيّرت التذكرة في هذه الأثناء — أعد التحميل ثم حاول مجددًا.",
  "mgmt.support.wf.triage.error": "فشل الإجراء. أعد المحاولة.",
  "mgmt.support.wf.triage.version": "الإصدار {v}",

  "mgmt.support.wf.resolve": "حل التذكرة",
  "mgmt.support.wf.resolve.title": "حل التذكرة {ref}",
  "mgmt.support.wf.resolve.note.label": "ملاحظة الحل",
  "mgmt.support.wf.resolve.note.placeholder": "صف الحل المُبلَّغ للمختبر…",
  "mgmt.support.wf.resolve.note.hint": "تُضاف الملاحظة إلى سلسلة المحادثة، ويراها المختبر.",
  "mgmt.support.wf.resolve.note.required": "ملاحظة الحل مطلوبة.",
  "mgmt.support.wf.resolve.confirm": "وضع علامة محلولة",
  "mgmt.support.wf.resolve.cancel": "إلغاء",
  "mgmt.support.wf.resolve.done": "تم حل التذكرة.",
  "mgmt.support.wf.resolve.closed": "هذه التذكرة مغلقة — أعد فتحها قبل حلها.",

  "mgmt.support.wf.sla.title": "تجاوزات اتفاقية مستوى الخدمة (عبر المختبرات)",
  "mgmt.support.wf.sla.none": "لا توجد تجاوزات. جميع التذاكر المفتوحة ضمن المهلة.",
  "mgmt.support.wf.sla.scanned": "التذاكر المفتوحة المفحوصة",
  "mgmt.support.wf.sla.breached": "متجاوِزة",
  "mgmt.support.wf.sla.emailed": "التنبيهات المُرسلة",
  "mgmt.support.wf.sla.col.reference": "المرجع",
  "mgmt.support.wf.sla.col.sujet": "الموضوع",
  "mgmt.support.wf.sla.col.priorite": "الأولوية",
  "mgmt.support.wf.sla.col.overdue": "التأخير",
  "mgmt.support.wf.sla.col.due": "الأجل",
  "mgmt.support.wf.sla.col.emailed": "أُنذر",
  "mgmt.support.wf.sla.overdue.hours": "{h} ساعة تأخير",
  "mgmt.support.wf.sla.overdue.aria": "التذكرة {ref} متجاوِزة بـ {h} ساعة",
  "mgmt.support.wf.sla.emailed.yes": "نعم",
  "mgmt.support.wf.sla.emailed.no": "لا",
  "mgmt.support.wf.sla.notify": "إشعار المختبرات",
  "mgmt.support.wf.sla.notifying": "جارٍ الإرسال…",
  "mgmt.support.wf.sla.notified": "تم إرسال {n} تنبيه(ات).",
  "mgmt.support.wf.sla.notified.none": "لا توجد تنبيهات جديدة للإرسال.",
  "mgmt.support.wf.sla.notify.error": "فشل إرسال التنبيهات. أعد المحاولة.",
  "mgmt.support.wf.sla.open": "فتح",
  "mgmt.support.wf.sla.more": "و{n} تذكرة أخرى متجاوِزة",
};

let registered = false;
export function registerSupportWorkflowMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
